// ---------------------------------------------------------------------------
// Panneau de reglages des sous-titres.
//
// Il s'ancre en bas a droite, au-dessus du bouton qui l'ouvre, et repartit ses
// reglages en onglets. La version precedente empilait une trentaine de champs
// dans un tiroir pleine hauteur : tout etait visible d'un coup, donc rien ne
// l'etait vraiment.
//
// Le contenu est genere plutot qu'ecrit en dur — une ligne de description par
// reglage, au lieu de deux fichiers a tenir en phase.
// ---------------------------------------------------------------------------

import { DEFAUTS, FAMILLES, policesLocales } from './soustitres.js';
import { RESERVE_POLICES, chargerReserve } from './polices-reserve.js';

// Les bornes de l'écart au bord : celles que `appliquerStyle` tient (le texte
// ne sort jamais de l'image). Le curseur allait de 0 à 40 alors que tout ce
// qui était sous 6 ne bougeait rien — un curseur qui ment sur un quart de sa
// course (Toji, 21/10 : « le réglage de la position n'est pas très bien »).
export const ECART_MIN = 6;
export const ECART_MAX = 40;
const CLES_POSITION = ['position', 'decalage', 'largeur', 'alignement', 'interligne'];

const ALIGNEMENTS = [['left', 'Gauche'], ['center', 'Centre'], ['right', 'Droite']];
const POSITIONS = [['bas', 'Bas'], ['milieu', 'Milieu'], ['haut', 'Haut']];
const GRAISSES = [[300, 'Fine'], [400, 'Normale'], [600, 'Semi-grasse'], [700, 'Grasse'], [900, 'Noire']];
const OMBRES = [['aucune', 'Aucune'], ['douce', 'Douce'], ['marquee', 'Marquée'], ['relief', 'Relief']];

const echapper = (s) => String(s).replace(/[&<>"]/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const curseur = ({ cle, libelle, min, max, pas, suffixe }) => `<label class="st-champ st-glissiere">
    <span>${libelle}<output data-sortie="${cle}"></output></span>
    <input type="range" data-cle="${cle}" min="${min}" max="${max}" step="${pas}"
           data-suffixe="${suffixe ?? ''}" />
  </label>`;

const choix = ({ cle, libelle, options }) => `<label class="st-champ">
    <span>${libelle}</span>
    <select data-cle="${cle}">
      ${options.map(([v, t]) => `<option value="${echapper(v)}">${echapper(t)}</option>`).join('')}
    </select>
  </label>`;

const couleur = ({ cle, libelle }) => `<label class="st-champ st-teinte">
    <span>${libelle}</span><input type="color" data-cle="${cle}" />
  </label>`;

const bascule = ({ cle, libelle }) => `<label class="st-champ st-case">
    <input type="checkbox" data-cle="${cle}" /><span>${libelle}</span>
  </label>`;

// Un onglet = un titre court et son contenu. L'ordre suit celui dans lequel on
// regle : d'abord QUOI afficher, puis comment, puis ou.
const ONGLETS = [
  {
    cle: 'piste', nom: 'Piste',
    corps: () => `<div class="st-pistes" data-pistes></div>
      ${curseur({ cle: 'decalageTemps', libelle: 'Synchronisation', min: -10, max: 10, pas: 0.1, suffixe: ' s' })}
      <p class="st-note">Valeur négative si les sous-titres passent en retard sur l'image.</p>`,
  },
  {
    cle: 'texte', nom: 'Texte',
    // La police se choisit dans un petit menu (Toji, 21/10 : « un vrai petit
    // menu ») : recherche, interrupteurs de famille, chaque nom dans sa police.
    corps: () => `<div class="st-champ"><span>Police</span>
      <button type="button" class="st-select" data-ouvrir-polices aria-haspopup="listbox" aria-expanded="false">
        <span data-police-nom></span><svg class="st-chevron" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg></button></div>
      <button type="button" class="st-lien" data-locales>Ajouter les polices de mon PC</button>
      <p class="st-note" data-note hidden></p>
      ${curseur({ cle: 'taille', libelle: 'Taille', min: 50, max: 320, pas: 5, suffixe: '%' })}
      ${choix({ cle: 'graisse', libelle: 'Graisse', options: GRAISSES })}
      ${couleur({ cle: 'couleur', libelle: 'Couleur' })}
      ${curseur({ cle: 'opacite', libelle: 'Opacité', min: 20, max: 100, pas: 1, suffixe: '%' })}
      ${bascule({ cle: 'italique', libelle: 'Italique' })}
      ${bascule({ cle: 'majuscules', libelle: 'Majuscules' })}`,
  },
  {
    cle: 'style', nom: 'Contour',
    corps: () => `${curseur({ cle: 'contour', libelle: 'Épaisseur du contour', min: 0, max: 8, pas: 0.2, suffixe: ' px' })}
      ${couleur({ cle: 'couleurContour', libelle: 'Couleur du contour' })}
      ${choix({ cle: 'ombre', libelle: 'Ombre portée', options: OMBRES })}
      <hr class="st-filet" />
      ${couleur({ cle: 'fond', libelle: 'Cartouche' })}
      ${curseur({ cle: 'opaciteFond', libelle: 'Opacité du fond', min: 0, max: 100, pas: 1, suffixe: '%' })}
      ${curseur({ cle: 'rayonFond', libelle: 'Arrondi', min: 0, max: 20, pas: 1, suffixe: ' px' })}`,
  },
  {
    cle: 'place', nom: 'Position',
    corps: () => `${choix({ cle: 'position', libelle: 'Position', options: POSITIONS })}
      ${curseur({ cle: 'decalage', libelle: 'Écart au bord', min: ECART_MIN, max: ECART_MAX, pas: 1, suffixe: '%' })}
      ${curseur({ cle: 'largeur', libelle: 'Largeur maximale', min: 40, max: 100, pas: 1, suffixe: '%' })}
      ${choix({ cle: 'alignement', libelle: 'Alignement', options: ALIGNEMENTS })}
      ${curseur({ cle: 'interligne', libelle: 'Interligne', min: 1, max: 2.4, pas: 0.05 })}
      <button type="button" class="st-lien" data-position-defaut>Position par défaut</button>`,
  },
];


// Le menu des polices. Les interrupteurs s'additionnent : aucun = toutes les
// polices, « Empattement » seul = les polices à empattement, etc.
const LOUPE = '<svg class="st-loupe" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.6"/><path d="m10.4 10.4 3.4 3.4"/></svg>';
const COCHE = '<svg class="st-coche" viewBox="0 0 16 16" aria-hidden="true"><path d="m3.5 8.4 2.9 2.9 6.1-6.6"/></svg>';
const interrupteur = (cle, libelle) => `<button type="button" class="st-inter" role="switch" aria-checked="false" data-inter="${cle}"><span>${libelle}</span><i aria-hidden="true"></i></button>`;
const MENU_POLICES = `<div class="st-pop" data-pop hidden role="dialog" aria-label="Choisir une police">
    <label class="st-pol-quete">${LOUPE}<input type="search" data-recherche placeholder="Chercher une police"
      aria-label="Chercher une police" autocomplete="off" spellcheck="false" /></label>
    <div class="st-inters">${interrupteur('serif', 'Empattement')}${interrupteur('mono', 'Fixe')}${interrupteur('deco', 'Déco')}</div>
    <div class="st-pol-liste" data-polices role="listbox" aria-label="Police" tabindex="-1"></div></div>`;

export function construirePanneau({ panneau, video, obtenir, definir, rendu, surApercu }) {
  if (!panneau) return null;

  // Les familles du système (FAMILLES) et la réserve servie par ce domaine,
  // rangées par catégorie ; « Mon PC » s'ajoute à la demande.
  const CAT_DE = { 'Sans serif': 'sans', Serif: 'serif', Affiche: 'affiche', 'Chasse fixe': 'mono', Manuscrite: 'manuscrite' };
  const catalogue = [];
  const ajouter = (famille, cat) => {
    if (!catalogue.some((p) => p.famille.toLowerCase() === famille.toLowerCase())) catalogue.push({ famille, categorie: cat });
  };
  for (const p of RESERVE_POLICES) ajouter(p.famille, p.categorie);
  for (const fa of FAMILLES) for (const nom of fa.polices) ajouter(nom, CAT_DE[fa.nom] ?? 'sans');
  let recherche = '';

  panneau.innerHTML = `
    <div class="st-entete">
      <span class="st-titre">Sous-titres</span>
      <button type="button" class="st-fermer" data-fermer aria-label="Fermer">✕</button>
    </div>
    <div class="st-onglets" role="tablist">
      ${ONGLETS.map((o, i) => `<button type="button" role="tab" data-onglet="${o.cle}"
        aria-selected="${i === 0}"${i === 0 ? ' class="actif"' : ''}>${o.nom}</button>`).join('')}
    </div>
    <div class="st-corps">
      ${ONGLETS.map((o, i) => `<section data-vue="${o.cle}"${i ? ' hidden' : ''}>${o.corps()}</section>`).join('')}
    </div>
    <div class="st-pied">
      <button type="button" class="st-defaut" data-defaut>Réglages d'origine</button>
    </div>${MENU_POLICES}`;

  const q = (s) => panneau.querySelector(s);
  const listePolices = q('[data-polices]');
  const champRecherche = q('[data-recherche]');

  // --- onglets ---------------------------------------------------------------
  panneau.querySelectorAll('[data-onglet]').forEach((b) => {
    b.onclick = () => {
      panneau.querySelectorAll('[data-onglet]').forEach((autre) => {
        const actif = autre === b;
        autre.classList.toggle('actif', actif);
        autre.setAttribute('aria-selected', String(actif));
      });
      panneau.querySelectorAll('[data-vue]').forEach((v) => {
        v.hidden = v.dataset.vue !== b.dataset.onglet;
      });
    };
  });

  // --- polices ---------------------------------------------------------------
  const sansAccent = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const actifs = new Set(); // interrupteurs allumés : 'serif', 'mono', 'deco'
  const genre = (c) => (c === 'manuscrite' || c === 'affiche' ? 'deco' : c);
  const garde = (p) => !actifs.size || actifs.has(genre(p.categorie));

  const vue = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((entrees) => {
      for (const e of entrees) {
        if (!e.isIntersecting) continue;
        e.target.style.fontFamily = `"${e.target.dataset.police}", system-ui, sans-serif`;
        vue.unobserve(e.target);
      }
    }, { root: listePolices, rootMargin: '40px 0px' })
    : null;

  function peindreNom() {
    const n = q('[data-police-nom]');
    if (!n) return;
    const p = obtenir().police;
    n.textContent = p;
    n.style.fontFamily = `"${p}", system-ui, sans-serif`;
  }

  function peindrePolices() {
    const mot = sansAccent(recherche.trim());
    const actuelle = obtenir().police;
    const visibles = catalogue.filter((p) => garde(p) && (!mot || sansAccent(p.famille).includes(mot)));
    vue?.disconnect();
    listePolices.innerHTML = visibles.length
      ? visibles.map((p) => `<button type="button" class="st-pol" role="option" data-police="${echapper(p.famille)}" aria-selected="${
        p.famille === actuelle}"><span>${echapper(p.famille)}</span>${COCHE}</button>`).join('')
      : '<p class="st-note st-pol-vide">Aucune police ne correspond.</p>';
    listePolices.querySelectorAll('[data-police]').forEach((b) => {
      vue?.observe(b);
      b.onclick = () => {
        definir({ ...obtenir(), police: b.dataset.police });
        listePolices.querySelectorAll('[data-police]').forEach((n) => n.setAttribute('aria-selected', String(n === b)));
        peindreNom();
      };
    });
    peindreNom();
  }

  champRecherche.oninput = () => { recherche = champRecherche.value; peindrePolices(); };
  panneau.querySelectorAll('[data-inter]').forEach((b) => {
    b.onclick = () => {
      const on = b.getAttribute('aria-checked') !== 'true';
      b.setAttribute('aria-checked', String(on));
      if (on) actifs.add(b.dataset.inter); else actifs.delete(b.dataset.inter);
      peindrePolices();
    };
  });
  // Le menu se pose sous la rangée « Police », dans le panneau. Échap, un clic
  // ailleurs ou un second clic sur la rangée le referment, avant le panneau.
  const pop = q('[data-pop]');
  const declencheur = q('[data-ouvrir-polices]');
  function ouvrirChoix() {
    const ligne = declencheur.closest('.st-champ');
    const haut = ligne.offsetTop + ligne.offsetHeight - q('.st-corps').scrollTop + 4;
    pop.style.top = `${haut}px`;
    pop.style.maxHeight = `${Math.max(200, panneau.clientHeight - haut - 8)}px`;
    pop.hidden = false;
    declencheur.setAttribute('aria-expanded', 'true');
    peindrePolices();
    const choisie = listePolices.querySelector('[aria-selected="true"]');
    if (choisie) listePolices.scrollTop = choisie.offsetTop - listePolices.clientHeight / 2;
    champRecherche.focus({ preventScroll: true });
  }
  function fermerChoix() {
    if (pop.hidden) return false;
    pop.hidden = true;
    declencheur.setAttribute('aria-expanded', 'false');
    declencheur.focus({ preventScroll: true });
    return true;
  }
  declencheur.onclick = () => { if (!fermerChoix()) ouvrirChoix(); };
  panneau.addEventListener('pointerdown', (e) => {
    if (!pop.hidden && !pop.contains(e.target) && !declencheur.contains(e.target)) fermerChoix();
  });
  // Échap dans le menu (recherche comprise) ferme le menu, pas le panneau.
  panneau.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && fermerChoix()) { e.stopPropagation(); e.preventDefault(); }
  });

  q('[data-locales]').onclick = async () => {
    const note = q('[data-note]');
    const r = await policesLocales();
    note.hidden = false;
    if (!r.ok) {
      note.textContent = `Polices du système indisponibles : ${r.raison}.`;
      return;
    }
    // Elles arrivent dans leur propre categorie : les melanger aux listes
    // existantes rendrait le choix ingerable.
    for (const nom of r.familles) ajouter(nom, 'pc');
    peindrePolices();
    note.textContent = `${r.familles.length} polices ajoutées dans « Mon PC ».`;
  };

  // --- pistes ----------------------------------------------------------------
  //
  // ⚠️ VERROU ANTI-REBOND : DEUX CLICS RAPPROCHÉS NE DOIVENT PAS LAISSER DEUX
  // PISTES ACTIVES.
  //
  // `window.__activerPisteST` (posé par `main.js`) bascule `mode` de façon
  // SYNCHRONE sur toutes les pistes dès l'appel, puis va chercher les
  // répliques par un `fetch` qu'on ne peut pas suivre d'ici — le passe-plat ne
  // renvoie aucune promesse, alors que `activerPiste`, côté `main.js`, est
  // asynchrone — et ne repose `mode` sur la piste choisie qu'APRÈS ce fetch,
  // sans revérifier qu'aucun autre choix n'a été fait entretemps. Deux clics
  // rapprochés sur deux pistes différentes lancent donc deux fetch en
  // parallèle ; celui qui répond en second écrase le `mode` posé par le
  // premier, et rien ne garantit plus qu'une seule piste reste 'hidden'.
  //
  // Faute de promesse à attendre, le verrou se lève au premier signe que
  // quelque chose a changé sur les pistes (`addtrack`/`removetrack`, déjà
  // écoutés plus bas) — ou, à défaut, après un délai de sécurité.
  //
  // ⚠️ CE DÉLAI N'EST PAS LA DURÉE DU CHARGEMENT, ET NE DOIT JAMAIS ÊTRE LU
  // COMME TEL : il ne protège que du double-clic. Voir `pisteEnVol` juste en
  // dessous — c'est lui qui porte l'attente réelle.
  let verrouPiste = false;
  let minuteurVerrouPiste = null;
  const DELAI_VERROU_PISTE = 4000; // ms : anti-rebond, RIEN DE PLUS

  function leverVerrouPiste() {
    if (!verrouPiste) return;
    verrouPiste = false;
    clearTimeout(minuteurVerrouPiste);
    minuteurVerrouPiste = null;
  }

  // ⚠️ DÉFAUT PAYÉ LE 14/09 : « DES SOUS-TITRES QUI APPARAISSENT 30 ANS APRÈS ».
  //
  // Le verrou ci-dessus servait à la fois de garde-fou ET d'unique signe visible
  // qu'il se passait quelque chose : le bouton se cochait (`activerPiste` pose
  // `mode = 'hidden'` de façon SYNCHRONE, main.js), les boutons se grisaient
  // 4 secondes, puis redevenaient cliquables — et la minute suivante était
  // MUETTE. Pire : le minuteur de 4 s était le SEUL chemin de levée sur ce
  // parcours. `activerPiste` n'ajoute ni ne retire aucune piste — il pose un
  // `blob:` sur la balise déjà en place —, donc `addtrack` ne part jamais, et
  // le commentaire d'origine (« au-delà, on suppose le fetch en échec ») se
  // trompait d'un facteur 15 à 80 : l'attente mesurée est de 60 à 320 s.
  // L'écran disait « c'est fait » pendant que le fetch était encore en vol.
  //
  // On sépare donc les deux rôles, qui n'ont ni la même durée ni le même but :
  //   · `verrouPiste` reste l'anti-rebond COURT — c'est lui qui grise, et il
  //     doit rester court : `activerPiste` n'a ni timeout ni `AbortSignal`, un
  //     fetch qui pend laisserait sinon les boutons morts pour toujours. On
  //     remplacerait une attente muette par une interface morte ;
  //   · `pisteEnVol` porte l'ATTENTE, aussi longue soit-elle, et l'AFFICHE.
  //
  // Fin du vol, par ordre de fiabilité :
  //   1. la promesse rendue par `window.__activerPisteST`, SI le passe-plat en
  //      rend une — et seulement si la piste a effectivement des répliques :
  //      `activerPiste` sort tôt quand `cible.chargee` est déjà vrai, drapeau
  //      posé AVANT le fetch. Une promesse résolue ne prouve donc rien à elle
  //      seule : sur un re-clic pendant le vol, elle dirait « terminé » à tort ;
  //   2. à défaut, l'arrivée des répliques : le `blob:` posé sur la balise fait
  //      remplir `piste.cues`, seul témoin local et honnête de l'aboutissement ;
  //   3. en dernier ressort une échéance — qui ne remet PAS l'écran en silence,
  //      mais écrit pourquoi elle renonce (journal compris).
  let pisteEnVol = null;       // rang de la piste dont les répliques sont en route
  let minuteurEnVol = null;    // sonde qui guette leur arrivée
  let echeanceEnVol = 0;
  let noteEnVol = '';          // ce qu'on dit quand un vol s'est mal terminé
  const PAS_SONDE_EN_VOL = 250;         // ms entre deux regards sur `cues`
  const DELAI_ABANDON_EN_VOL = 360000;  // ms : au-delà de l'attente la plus longue mesurée (320 s)

  // `cues` vaut `null` tant que la piste est `disabled` ; `activerPiste` la
  // passe en `hidden` avant de partir chercher, donc pendant le vol c'est une
  // liste VIDE, et pleine une fois le `blob:` analysé. D'où ce test-là.
  const aDesRepliques = (rang) => {
    const piste = [...video.textTracks][rang];
    return !!(piste && piste.cues && piste.cues.length);
  };

  function finirLeVol(note = '') {
    clearTimeout(minuteurEnVol);
    minuteurEnVol = null;
    pisteEnVol = null;
    noteEnVol = note;
    if (!panneau.hidden) peindrePistes();
  }

  function surveillerLeVol(rang) {
    clearTimeout(minuteurEnVol);
    pisteEnVol = rang;
    noteEnVol = '';
    echeanceEnVol = Date.now() + DELAI_ABANDON_EN_VOL;
    const regarder = () => {
      if (pisteEnVol !== rang) return; // un choix plus récent a pris la main
      if (aDesRepliques(rang)) { finirLeVol(); return; }
      if (Date.now() >= echeanceEnVol) {
        // Pas d'abandon muet : le journal dit ce qu'on a attendu et combien.
        console.warn(`[st-panneau] piste ${rang} : aucune réplique après ${DELAI_ABANDON_EN_VOL} ms, on cesse d'annoncer un chargement`);
        finirLeVol('Cette piste ne répond pas — réessayez, ou choisissez-en une autre.');
        return;
      }
      minuteurEnVol = setTimeout(regarder, PAS_SONDE_EN_VOL);
    };
    minuteurEnVol = setTimeout(regarder, PAS_SONDE_EN_VOL);
  }

  function peindrePistes() {
    const zone = q('[data-pistes]');
    const pistes = [...video.textTracks];
    if (!pistes.length) {
      // « Aucun » est un VERDICT, et il n'est pas encore rendu : la liste des
      // pistes se demande au serveur, qui ouvre le fichier pour repondre. Tant
      // qu'elle n'est pas revenue, on dit qu'on cherche.
      //
      // ⚠️ CE DRAPEAU N'EST POSÉ NULLE PART AUJOURD'HUI.
      //
      // `video.dataset.stCherche` n'est lu qu'ici : rien, dans `main.js`, ne
      // l'écrit jamais à '1' avant de lancer `chargerSousTitresDeSecours` (la
      // recherche de secours, qui peut prendre jusqu'à 48 s — 8 essais espacés
      // de 6 s), ni ne le retire à la fin. Tant que ce drapeau n'existe pas
      // vraiment côté `main.js`, cette branche affiche toujours « Aucun
      // sous-titre dans cette source » — un VERDICT DÉFINITIF ET FAUX pendant
      // toute la recherche. Voir le rapport de cette correction pour la ligne
      // exacte à poser dans `main.js` (fonctions `chargerSousTitres` et
      // `chargerSousTitresDeSecours`).
      //
      // ROBUSTESSE APPORTÉE ICI : l'observateur juste après `peindrePistes`
      // (plus bas dans ce fichier) surveille ce même attribut et repeint dès
      // qu'il change pendant que le panneau est ouvert — pour que, LE JOUR OÙ
      // `main.js` pose et retire ce drapeau, l'écran suive en direct au lieu
      // d'exiger un aller-retour fermer/rouvrir comme pour les pistes.
      zone.innerHTML = video.dataset.stCherche === '1'
        ? '<p class="st-note">Recherche des sous-titres…</p>'
        : '<p class="st-note">Aucun sous-titre dans cette source.</p>';
      return;
    }
    // ⚠️ HYPOTHESE SILENCIEUSE : UNE SEULE PISTE ACTIVE A LA FOIS.
    //
    // `findIndex` s'arrête à la première piste en mode 'hidden', dans l'ORDRE
    // du fichier — jamais forcément la dernière choisie. En service normal
    // c'est sans effet (une seule piste EST active) ; mais si le verrou plus
    // haut ne suffisait pas à empêcher la course décrite au-dessus, c'est ce
    // choix, déterministe mais pas nécessairement le bon, qui déciderait quel
    // bouton s'affiche coché.
    const actif = pistes.findIndex((p) => p.mode === 'hidden');
    // ⚠️ UN BOUTON COCHÉ NE VEUT PAS DIRE « PRÊT ». Le `mode` est posé tout de
    // suite, les répliques arrivent une minute plus tard : tant que le vol dure
    // (`pisteEnVol`), le bouton le DIT, au lieu de laisser croire que c'est fait.
    const nommer = (p, i) => echapper(p.label || (p.language || '').toUpperCase() || `Piste ${i + 1}`);
    zone.innerHTML = [
      `<button type="button" data-piste="-1"${actif < 0 ? ' class="actif"' : ''}${verrouPiste ? ' disabled' : ''}>Aucun</button>`,
      ...pistes.map((p, i) => {
        const enVol = i === pisteEnVol;
        const classes = [i === actif ? 'actif' : '', enVol ? 'st-en-vol' : ''].filter(Boolean).join(' ');
        return `<button type="button" data-piste="${i}"${classes ? ` class="${classes}"` : ''}${
          verrouPiste ? ' disabled' : ''}${enVol ? ' aria-busy="true"' : ''}>${
          nommer(p, i)}</button>`;
      }),
      // Le bouton porte l'état ; cette ligne porte l'ATTENTE — elle seule
      // explique pourquoi rien ne s'affiche encore à l'image.
      // ⚠️ UNE LIGNE TOUJOURS LÀ, MÊME VIDE (Toji, 21/10 : « rien ne doit
      // bouger »). Elle apparaissait et disparaissait, et le libellé du bouton
      // s'allongeait de « — chargement… » : le panneau changeait de hauteur et
      // les boutons voisins se décalaient sous la souris.
      pisteEnVol !== null
        ? '<p class="st-note st-vol" data-en-vol>Chargement de la piste… cela peut prendre une minute.</p>'
        : noteEnVol ? `<p class="st-note st-vol" data-echec-vol>${echapper(noteEnVol)}</p>`
          : '<p class="st-note st-vol" aria-hidden="true"></p>',
    ].join('');
    zone.querySelectorAll('button').forEach((b) => {
      b.onclick = () => {
        // Un chargement est déjà en vol : ce second clic est ignoré plutôt que
        // mis en file — les boutons sont de toute façon dessinés `disabled`
        // pendant ce temps, ceci n'est qu'un filet pour un clic arrivé juste
        // avant le repeint.
        if (verrouPiste) return;
        const rang = Number(b.dataset.piste);
        // ⚠️ CHOISIR UNE PISTE, C'EST AUSSI ALLER LA CHERCHER.
        //
        // Poser `mode` ne suffit plus : les repliques ne viennent plus d'une
        // balise `<track>` — que le navigateur telechargeait quand il le
        // decidait, et souvent jamais — mais d'un `fetch` explicite. Sans cet
        // appel, choisir « FRE » cochait le bouton et n'affichait rien : la
        // piste existait, vide, et rien ne disait pourquoi. C'est exactement ce
        // que Toji voyait.
        //
        // `hidden` fait decoder la piste SANS l'afficher : c'est ce mode qui
        // nous laisse la peindre nous-memes.
        if (rang >= 0 && typeof window.__activerPisteST === 'function') {
          // Le verrou ne couvre QUE le double-clic, et seulement 4 s — voir le
          // commentaire au-dessus de `peindrePistes`.
          verrouPiste = true;
          clearTimeout(minuteurVerrouPiste);
          minuteurVerrouPiste = setTimeout(() => {
            leverVerrouPiste();
            if (!panneau.hidden) peindrePistes();
          }, DELAI_VERROU_PISTE);
          // L'attente, elle, commence ici et dure ce qu'elle dure : c'est elle
          // qu'on affiche, faute de quoi l'écran ment pendant une minute.
          surveillerLeVol(rang);
          const retour = window.__activerPisteST(pistes[rang]);
          if (retour && typeof retour.then === 'function') {
            // Le passe-plat rend une promesse : c'est le signal le plus juste
            // dont on dispose — mais pas une preuve à lui seul, voir plus haut.
            retour.then(
              () => {
                if (pisteEnVol === rang && aDesRepliques(rang)) finirLeVol();
              },
              (err) => {
                // ⚠️ AUCUN `catch` MUET : une piste qui échoue doit le dire, à
                // l'écran ET au journal. C'est exactement ce silence-là qui
                // laissait Toji devant un bouton coché et une image sans texte.
                console.warn(`[st-panneau] piste ${rang} : chargement échoué`, err);
                if (pisteEnVol === rang) finirLeVol('Cette piste n’a pas pu être chargée.');
              },
            );
          }
        } else {
          pistes.forEach((p, i) => { p.mode = i === rang ? 'hidden' : 'disabled'; });
          if (rang < 0) window.__retenirST?.('aucun'); // « Aucun » est un choix, à retenir comme une langue
          // « Aucun », ou pas de passe-plat : plus rien n'est en route, et ce
          // qui l'était ne concerne plus ce qu'on affiche.
          finirLeVol();
        }
        peindrePistes();
        rendu.peindre();
      };
    });
  }

  // --- champs ----------------------------------------------------------------
  function peindreValeurs() {
    const r = obtenir();
    panneau.querySelectorAll('[data-cle]').forEach((n) => {
      const v = r[n.dataset.cle];
      if (n.type === 'checkbox') n.checked = !!v;
      else n.value = String(v);
      const sortie = panneau.querySelector(`[data-sortie="${n.dataset.cle}"]`);
      if (sortie) sortie.textContent = `${v}${n.dataset.suffixe ?? ''}`;
    });
    peindrePolices();
    // Milieu : l'écart au bord n'a pas de bord auquel se mesurer.
    const ecart = panneau.querySelector('[data-cle="decalage"]');
    if (ecart) ecart.disabled = r.position === 'milieu';
  }

  panneau.addEventListener('input', (e) => {
    const n = e.target.closest('[data-cle]');
    if (!n) return;
    const cle = n.dataset.cle;
    let v;
    if (n.type === 'checkbox') v = n.checked;
    else if (n.type === 'range') v = Number(n.value);
    else v = /^-?\d+(\.\d+)?$/.test(n.value) ? Number(n.value) : n.value;

    if (cle === 'decalage') v = Math.min(ECART_MAX, Math.max(ECART_MIN, v));
    definir({ ...obtenir(), [cle]: v });
    if (cle === 'position') peindreValeurs();
    const sortie = panneau.querySelector(`[data-sortie="${cle}"]`);
    if (sortie) sortie.textContent = `${v}${n.dataset.suffixe ?? ''}`;
  });

  // Un clic dans le panneau ne doit jamais atteindre le lecteur : sinon regler
  // un curseur mettrait la video en pause.
  panneau.addEventListener('click', (e) => e.stopPropagation());

  q('[data-defaut]').onclick = () => { definir({ ...DEFAUTS }); peindreValeurs(); };
  q('[data-position-defaut]').onclick = () => {
    definir({ ...obtenir(), ...Object.fromEntries(CLES_POSITION.map((k) => [k, DEFAUTS[k]])) });
    peindreValeurs();
  };
  q('[data-fermer]').onclick = () => fermer();

  // --- ouverture -------------------------------------------------------------
  function ouvrir() {
    panneau.hidden = false;
    // La feuille de la réserve (des @font-face, aucun fichier) : les woff2 ne
    // partent qu'à l'aperçu d'un nom ou au choix d'une police.
    chargerReserve();
    peindreValeurs();
    peindrePistes();
    // ⚠️ ON NE MET PLUS LA LECTURE EN PAUSE.
    //
    // Toji : « pourquoi, quand j'appuie sur le bouton de sous-titres, ça me met
    // en pause ? » L'intention etait bonne — « les reglages se jugent sur
    // l'image, pas dans le vide » — mais on n'ouvre pas ce panneau QUE pour
    // regler : neuf fois sur dix on vient CHOISIR une piste, pendant que
    // l'episode tourne. Couper la lecture pour ca, c'est repondre a une
    // question qu'on n'a pas posee — et en seance, c'est couper pour tout le
    // monde.
    //
    // L'apercu reste : le texte d'exemple s'affiche sur l'image qui bouge,
    // ce qui juge le contour et la position aussi bien, sinon mieux.
    surApercu?.(true);
  }

  function fermer() {
    if (!pop.hidden) { pop.hidden = true; declencheur.setAttribute('aria-expanded', 'false'); }
    panneau.hidden = true;
    surApercu?.(false);
  }

  // ⚠️ LES PISTES ARRIVENT APRES, ET LE PANNEAU DOIT LE VOIR.
  //
  // Toji : « il n'y a aucun sous-titre. Si je ferme l'onglet et que je le
  // remets, là, il y a les sous-titres qui apparaissent. »
  //
  // Lister les pistes d'un fichier coute un ffprobe — 2,7 s mesurees en
  // production. Le panneau, lui, s'ouvre tout de suite : il peignait « Aucun
  // sous-titre dans cette source » avec raison, puis ne repassait jamais. Seul
  // un aller-retour fermer/rouvrir le remettait a jour, ce qui donne un ecran
  // qui ment jusqu'a ce qu'on insiste.
  video.textTracks.addEventListener?.('addtrack', () => {
    // Une piste vient d'apparaître : c'est le signe le plus fiable que le
    // fetch en vol (voir le verrou plus haut) a abouti. On le lève ici plutôt
    // que d'attendre le délai de sécurité.
    leverVerrouPiste();
    if (!panneau.hidden) peindrePistes();
  });
  video.textTracks.addEventListener?.('removetrack', () => {
    leverVerrouPiste();
    if (!panneau.hidden) peindrePistes();
  });

  // ⚠️ LE PANNEAU DOIT AUSSI VOIR LA RECHERCHE DE SECOURS EN DIRECT.
  //
  // Même défaut que « les pistes arrivent après » ci-dessus, mais pour le
  // drapeau `stCherche` plutôt que pour les pistes elles-mêmes : si `main.js`
  // pose et retire un jour `video.dataset.stCherche` autour de la recherche de
  // secours (jusqu'à 48 s, voir le commentaire dans `peindrePistes`), rien
  // dans CE fichier ne le voyait passer — le panneau, resté ouvert avant même
  // que la recherche commence, aurait continué d'afficher le message figé au
  // moment de l'ouverture. `data-*` se lit comme un attribut DOM ordinaire :
  // un `MutationObserver` ciblé dessus suffit, pas besoin d'un évènement
  // maison ni de sonder la valeur à intervalle régulier.
  new MutationObserver(() => { if (!panneau.hidden) peindrePistes(); })
    .observe(video, { attributes: true, attributeFilter: ['data-st-cherche'] });

  peindreValeurs();

  return {
    ouvrir,
    fermer,
    basculer: () => (panneau.hidden ? ouvrir() : fermer()),
    peindrePistes,
    estOuvert: () => !panneau.hidden,
  };
}
